import React from 'react';
import {
  Divider,
  Heading,
  type HeadingProps,
  Text,
  type TextProps,
} from '@wix/design-system';
import classNames from 'classnames';
import css from '../../components/Markdowns/markdowns.scss';
import { Blockquote } from '../../components/Markdowns/Blockquote';
import { CodeBlock } from '../../components/Markdowns/CodeBlock';
import { LinkMarkdown } from '../../components/Markdowns/LinkMarkdown';
import { ImageMarkdown } from '../../components/Markdowns/ImageMarkdown';

export const getMarkDownRenderers = (): Object => {
  enum WDSComponent {
    TEXT = 'text',
    HEADING = 'heading',
  }
  const TEXT_MARKDOWNS = {
    // for each text component, define an array of arrays, each array contains:
    // in index 0 the props passed to the WDS Text component,
    // in index 1 an array of HTML elements that should be rendered with these props
    [WDSComponent.HEADING]: [
      [{ weight: 'bold', size: 'large' }, ['h1']],
      [{ weight: 'bold', size: 'medium' }, ['h2']],
      [{ size: 'small' }, ['h3']],
      [{ size: 'tiny' }, ['h5']],
      [{ size: 'extraTiny' }, ['h6']],
    ],
    [WDSComponent.TEXT]: [
      [{ weight: 'bold', size: 'medium' }, ['strong']],
      [{ size: 'medium' }, ['u']],
      [{ size: 'medium' }, ['h4']],
    ],
  };
  const getTextFn = (textProps: TextProps, element: string) => {
    return ({ children }) => (
      <Text
        className={classNames(css.markdownText, css[element])}
        {...textProps}
      >
        {children}
      </Text>
    );
  };
  const getHeadingFn = (textProps: HeadingProps, element: string) => {
    return ({ children }) => (
      <Heading
        className={classNames(css.markdownHeading, css[element])}
        {...textProps}
      >
        {children}
      </Heading>
    );
  };

  const getMarkDownCompFn = (
    compType: WDSComponent,
  ): typeof getTextFn | typeof getHeadingFn => {
    switch (compType) {
      case WDSComponent.HEADING:
        return getHeadingFn;
      case WDSComponent.TEXT:
        return getTextFn;
      default:
        return getTextFn;
    }
  };

  const plainTextMarkdowns = {};
  Object.entries(TEXT_MARKDOWNS).forEach(([compType, markdowns]) => {
    const compFn = getMarkDownCompFn(compType as WDSComponent);
    markdowns.forEach((markdown) => {
      const [textProps, elements] = markdown;
      (elements as string[]).forEach(
        (element: string) =>
          (plainTextMarkdowns[element] = compFn(textProps as Object, element)),
      );
    });
  });

  return {
    ...plainTextMarkdowns,
    a: ({ href, children, title }) => (
      <LinkMarkdown href={href} title={title} content={children} />
    ),
    img: ({ src, alt, className, children }) => (
      <ImageMarkdown src={src} alt={alt} className={className}>
        {children}
      </ImageMarkdown>
    ),
    hr: () => (
      <div className={css.divider}>
        <Divider />
      </div>
    ),
    code: ({ className, children, inline, language }) => (
      <CodeBlock
        inline={inline}
        language={language ?? className}
        code={children}
      />
    ),
    ul: ({ children }) => (
      <ul className={classNames(css.markdownText, css.ul)}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className={classNames(css.markdownText, css.ol)} type="1">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className={classNames(css.markdownText, css.li)}>{children}</li>
    ),
    p: ({ children }) => (
      <p className={classNames(css.markdownText, css.p)}>{children}</p>
    ),
    blockquote: ({ children, className }) => (
      <Blockquote className={className} content={children} />
    ),
  };
};
